import { useEffect, useRef } from "react";
import {
  useSphere,
  useCylinder,
  useDistanceConstraint,
  usePointToPointConstraint,
} from "@react-three/cannon";
import { useDragConstraint } from "../../utils/drag";
import { SpotLight, useGLTF } from "@react-three/drei";
import { metalicLightMaterial } from "../../common/materials";
import { useTheme } from "../../common/hooks";
import { changeColor, changeIntensity } from "../../animations/lights";
import { Easing } from "@tweenjs/tween.js";
import { Object3D } from "three";

export function Lamp({ position, ...props }) {
  const { nodes, materials } = useGLTF("/models/lamp.glb");
  const target = new Object3D();
  const { theme } = useTheme();
  const spotLight = useRef(null);

  const [fixed] = useSphere(() => ({
    type: "Static",
    args: [0.1],
    position,
  }));

  const [lamp] = useCylinder(() => ({
    mass: 100,
    type: "Dynamic",
    args: [0.1, 0.45, 0.45, 16],
    angularDamping: 0.95,
    linearDamping: 0.95,
  }));

  const bind = useDragConstraint(lamp);

  useDistanceConstraint(fixed, lamp, {
    distance: 2,
    pivotA: [0, 0, 0],
    pivotB: [0, 2, 0],
    collideConnected: true,
  });
  usePointToPointConstraint(fixed, lamp, {
    pivotA: [0, 0, 0],
    pivotB: [0, 2, 0],
  });

  useEffect(() => {
    if (spotLight.current) {
      changeIntensity(spotLight.current, theme.lampLight.intensity, {
        easing: Easing.Linear.None,
      });
      changeColor(spotLight.current, theme.lampLight.color, {
        duration: 2000,
      });
    }
  }, [theme]);

  return (
    <group ref={lamp} {...bind} {...props}>
      <mesh
        name="lamp"
        geometry={nodes.Cylinder_1.geometry}
        material={metalicLightMaterial}
        position={[0, 0, 0]}
      />
      <mesh
        name="borders"
        geometry={nodes.Cylinder_2.geometry}
        material={materials.black}
        position={[0, 0, 0]}
      />

      <SpotLight
        target={target}
        ref={spotLight}
        color={theme.lampLight.color}
        intensity={theme.lampLight.intensity}
        power={theme.lampLight.intensity}
        castShadow
        shadow-bias={-0.001}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        penumbra={0.25}
        radiusTop={0.4}
        radiusBottom={40}
        distance={40}
        attenuation={4}
        angle={1.2}
        anglePower={2.5}
        opacity={0.5}
        position={[0, -0.2, 0]}
      />
      <primitive object={target} position={[0, -1, 0]} />
    </group>
  );
}
